import { useEffect, useState } from 'react'
import { RefreshCw, Trash2 } from 'lucide-react'
import { agendaLabService } from '../../services/api'

const initial = {
  fullName: '',
  phone: '',
  email: '',
  notes: '',
  invitationExpiresAtUtc: '',
  invitationIsOneTime: false,
  invitationIsActive: true
}

const asArray = (value) => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.data)) return value.data
  if (Array.isArray(value?.$values)) return value.$values
  return []
}

const toDateTimeLocalValue = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const offset = date.getTimezoneOffset()
  const localDate = new Date(date.getTime() - (offset * 60 * 1000))
  return localDate.toISOString().slice(0, 16)
}

const toUtcIsoOrNull = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

const formatExpiry = (value) => {
  if (!value) return 'Sin vencimiento'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Sin vencimiento'

  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date)
}

export default function AgendaLabClients() {
  const [clients, setClients] = useState([])
  const [form, setForm] = useState(initial)
  const [editingId, setEditingId] = useState(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [deletingId, setDeletingId] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    try {
      setError('')
      const result = await agendaLabService.getClients()
      setClients(asArray(result.data))
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar clientes.')
      setClients([])
    }
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm(initial)
    setEditingId(null)
  }

  const startEdit = (client) => {
    setError('')
    setSuccess('')
    setEditingId(client.id)
    setForm({
      fullName: client.fullName || '',
      phone: client.phone || '',
      email: client.email || '',
      notes: client.notes || '',
      invitationExpiresAtUtc: toDateTimeLocalValue(client.invitationExpiresAtUtc),
      invitationIsOneTime: client.invitationIsOneTime ?? false,
      invitationIsActive: client.invitationIsActive ?? true
    })
  }

  const submit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const payload = {
        ...form,
        invitationExpiresAtUtc: toUtcIsoOrNull(form.invitationExpiresAtUtc)
      }

      if (editingId) {
        await agendaLabService.updateClient(editingId, payload)
        setSuccess('Cliente actualizado correctamente.')
      } else {
        await agendaLabService.createClient(payload)
        setSuccess('Cliente creado correctamente.')
      }

      resetForm()
      await load()
    } catch (err) {
      setError(err?.message || (editingId ? 'No se pudo actualizar el cliente.' : 'No se pudo crear el cliente.'))
    } finally {
      setSaving(false)
    }
  }

  const removeClient = async (client) => {
    if (!client?.canDelete) return
    if (!window.confirm(`Eliminar a ${client.fullName}? Esta accion no se puede deshacer.`)) return

    try {
      setDeletingId(client.id)
      setError('')
      setSuccess('')
      await agendaLabService.deleteClient(client.id)
      if (editingId === client.id) {
        resetForm()
      }
      setSuccess('Cliente eliminado correctamente.')
      await load()
    } catch (err) {
      setError(err?.message || 'No se pudo eliminar el cliente.')
    } finally {
      setDeletingId(null)
    }
  }

  const regenerateInvitation = async (clientId) => {
    try {
      setError('')
      setSuccess('')
      await agendaLabService.regenerateInvitation(clientId)
      setSuccess('Invitacion regenerada correctamente.')
      await load()
    } catch (err) {
      setError(err?.message || 'No se pudo regenerar la invitacion.')
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
        <p className="mt-1 text-sm text-gray-500">
          Crea o edita cada cliente con sus datos de contacto, notas internas y configuracion de invitacion.
        </p>
      </div>

      {error && <div className="card border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="card border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{success}</div>}

      <form className="card grid grid-cols-1 gap-4 p-4 md:grid-cols-2" onSubmit={submit}>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Nombre completo</label>
          <input
            className="input"
            placeholder="Nombre del cliente"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Telefono</label>
          <input
            className="input"
            placeholder="Telefono de contacto"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            className="input"
            type="email"
            placeholder="correo@ejemplo.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Vencimiento de invitacion</label>
          <input
            className="input"
            type="datetime-local"
            value={form.invitationExpiresAtUtc}
            onChange={(e) => setForm({ ...form, invitationExpiresAtUtc: e.target.value })}
          />
          <p className="text-xs text-gray-500">Dejalo vacio si la invitacion no debe expirar.</p>
        </div>

        <div className="space-y-2 md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Notas</label>
          <textarea
            className="input min-h-[110px]"
            placeholder="Notas internas sobre el cliente"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.invitationIsOneTime}
            onChange={(e) => setForm({ ...form, invitationIsOneTime: e.target.checked })}
          />
          Invitacion de un solo uso
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.invitationIsActive}
            onChange={(e) => setForm({ ...form, invitationIsActive: e.target.checked })}
          />
          Invitacion activa
        </label>

        <div className="flex flex-wrap gap-3 md:col-span-2">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Agregar cliente'}
          </button>
          {editingId && (
            <button className="btn btn-secondary" type="button" onClick={resetForm}>
              Cancelar edicion
            </button>
          )}
        </div>
      </form>

      <div className="card p-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-4">Cliente</th>
              <th className="py-2 pr-4">Contacto</th>
              <th className="py-2 pr-4">Notas</th>
              <th className="py-2 pr-4">Invitacion</th>
              <th className="py-2 pr-4">Dependencias</th>
              <th className="py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id} className="border-t align-top">
                <td className="py-3 pr-4">
                  <div className="font-medium text-gray-900">{client.fullName}</div>
                  <div className="text-xs text-gray-500">{client.id}</div>
                </td>
                <td className="py-3 pr-4 text-gray-600">
                  <div>{client.phone || 'Sin telefono'}</div>
                  <div>{client.email || 'Sin email'}</div>
                </td>
                <td className="py-3 pr-4 text-gray-600">{client.notes || 'Sin notas'}</td>
                <td className="py-3 pr-4 text-gray-600">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium text-gray-900">{client.invitationCode}</div>
                    <button
                      className={client.canRegenerateInvitation !== false
                        ? 'inline-flex items-center gap-1 rounded-md border border-primary-200 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50'
                        : 'inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-400 cursor-not-allowed bg-gray-50'}
                      type="button"
                      disabled={client.canRegenerateInvitation === false}
                      title={client.canRegenerateInvitation === false
                        ? 'No se puede regenerar porque este cliente ya tiene invitaciones o reservas asociadas'
                        : 'Regenerar codigo de invitacion'}
                      onClick={() => regenerateInvitation(client.id)}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Regenerar
                    </button>
                  </div>
                  <div>{client.invitationIsActive ? 'Activa' : 'Inactiva'}</div>
                  <div>{client.invitationIsOneTime ? 'Un solo uso' : 'Uso multiple'}</div>
                  <div>{formatExpiry(client.invitationExpiresAtUtc)}</div>
                </td>
                <td className="py-3 pr-4 text-gray-600">
                  {client.dependencyCount > 0 ? `${client.dependencyCount} asociada(s)` : 'Sin dependencias'}
                </td>
                <td className="py-3 text-right">
                  <div className="flex justify-end gap-3">
                    <button
                      className="text-primary-600 hover:text-primary-700"
                      type="button"
                      onClick={() => startEdit(client)}
                    >
                      Editar
                    </button>
                    <button
                      className={client.canDelete ? 'text-red-600 hover:text-red-700' : 'text-gray-400 cursor-not-allowed'}
                      type="button"
                      disabled={!client.canDelete || deletingId === client.id}
                      title={client.canDelete ? 'Eliminar cliente' : 'No se puede eliminar porque tiene dependencias asociadas'}
                      onClick={() => removeClient(client)}
                    >
                      <Trash2 className="inline h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr>
                <td className="py-6 text-center text-gray-500" colSpan="6">
                  Aun no tienes clientes creados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
